import React, { useState, useEffect, useContext, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AuthContext } from '../../../../../context/AuthContext';
import { getSafetyTrainings, saveSafetyTraining, deleteSafetyTraining, getSafetyBasicInfo } from '../../../../../api/safetyAssessApi';
import SafetyTrainingModal from './SafetyTrainingModal';

function SafetyTrainingTab({ assessmentId, summaryData, setSummaryData, isLocked, onSave, projectInfo }) {
  const { user, token } = useContext(AuthContext);
  const userName = user?.UserName || user?.name || '박학송';
  const userPosition = user?.position || '차장';
  const userDisplayName = `${userName} ${userPosition}`;

  const [siteManagerId, setSiteManagerId] = useState('');
  useEffect(() => {
    const fetchSiteManager = async () => {
      try {
        const basicInfo = await getSafetyBasicInfo(token);
        if (basicInfo?.managerId) setSiteManagerId(basicInfo.managerId);
      } catch (e) { /* 무시 */ }
    };
    if (token) fetchSiteManager();
  }, [token]);

  const hasEditPermission = (doc) => {
    // 1. 최고 관리자 (admin)
    if (user?.role === 'admin') return true;

    const currentUserId = user?.userid || user?.userId;

    // 2. 사업장 기본 정보 설정에 등록된 원본 안전관리자
    if (siteManagerId && currentUserId === siteManagerId) return true;

    // 3. 프로젝트 등록 시 다른 사람으로 지정(변경)된 안전관리자 및 기안자
    if (projectInfo && (currentUserId === projectInfo.writeId || currentUserId === projectInfo.managerId)) {
      return true;
    }

    // 4. 해당 교육일지를 직접 작성한 본인
    if (doc && doc.author === userDisplayName) return true;

    return false;
  };

  // API로부터 받아올 교육일지 데이터 상태 관리
  const [trainings, setTrainings] = useState([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearchQuery, setAppliedSearchQuery] = useState('');
  const [searchCategory, setSearchCategory] = useState('ALL');
  const [selectedDoc, setSelectedDoc] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('WRITE'); // 'WRITE' or 'DETAIL'

  const [currentPage, setCurrentPage] = useState(1);
  const [hoveredInstructor, setHoveredInstructor] = useState(null);
  const [filePopover, setFilePopover] = useState({ id: null, x: 0, y: 0 });

  // 팝오버 바깥 영역 클릭 및 스크롤 시 자동 닫기
  useEffect(() => {
    if (!filePopover.id) return;
    const closePopover = () => setFilePopover({ id: null, x: 0, y: 0 });
    document.addEventListener('click', closePopover);
    window.addEventListener('scroll', closePopover, { capture: true });
    return () => {
      document.removeEventListener('click', closePopover);
      window.removeEventListener('scroll', closePopover, { capture: true });
    };
  }, [filePopover.id]);

  const formatBytes = (bytes) => {
    if (!bytes || isNaN(bytes) || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleDownload = async (file) => {
    try {
      let domain = '';
      if (user?.M_ComCode) {
        const { getManager } = await import('../../../../../api/managerApi');
        const manager = await getManager(user.M_ComCode, token);
        if (manager?.M_Domain) domain = /^https?:\/\//i.test(manager.M_Domain) ? manager.M_Domain : window.location.protocol + '//' + manager.M_Domain;
      }
      const url = domain + (file.path ? file.path.replace(/\\/g, '/') : '');
      const a = document.createElement('a');
      a.href = url;
      a.download = file.originalName || file.name || 'download';
      a.target = '_blank';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => document.body.removeChild(a), 100);
    } catch (e) {
      console.error(e);
      alert('다운로드 중 오류가 발생했습니다.');
    }
  };

  const handleView = async (file) => {
    try {
      let domain = '';
      if (user?.M_ComCode) {
        const { getManager } = await import('../../../../../api/managerApi');
        const manager = await getManager(user.M_ComCode, token);
        if (manager?.M_Domain) domain = /^https?:\/\//i.test(manager.M_Domain) ? manager.M_Domain : window.location.protocol + '//' + manager.M_Domain;
      }
      const url = domain + (file.path ? file.path.replace(/\\/g, '/') : '');
      window.open(url, '_blank');
    } catch (e) {
      console.error(e);
      alert('파일 열기 중 오류가 발생했습니다.');
    }
  };

  const handleZipDownload = async (files, title) => {
    try {
      let domain = '';
      if (user?.M_ComCode) {
        const { getManager } = await import('../../../../../api/managerApi');
        const manager = await getManager(user.M_ComCode, token);
        if (manager?.M_Domain) domain = /^https?:\/\//i.test(manager.M_Domain) ? manager.M_Domain : window.location.protocol + '//' + manager.M_Domain;
      }
      alert(`${files.length}개의 파일을 압축 중입니다. 잠시만 기다려 주세요...`);
      const JSZip = (await import('jszip')).default;
      const { saveAs } = await import('file-saver');
      const zip = new JSZip();
      const promises = files.map(async (f) => {
        const url = domain + (f.path ? f.path.replace(/\\/g, '/') : '');
        const response = await fetch(url);
        const blob = await response.blob();
        zip.file(f.originalName || f.name, blob);
      });
      await Promise.all(promises);
      const content = await zip.generateAsync({ type: 'blob' });
      const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      saveAs(content, `${title ? title.replace(/[/\\:*?"<>|]/g, '_') : '첨부파일'}_${dateStr}.zip`);
    } catch (e) {
      console.error(e);
      alert('압축 파일 생성 중 오류가 발생했습니다.');
    }
  };

  const fetchTrainings = useCallback(async (category, query) => {
    try {
      if (!assessmentId) return;
      const data = await getSafetyTrainings(assessmentId, token, category, query);
      if ((!data || data.length === 0) && !query && summaryData && (summaryData.instructor || summaryData.content)) {
        // 기존의 summaryData 백업 연동
        setTrainings([{
          id: 'train-init',
          title: '안전보건 교육일지 (이관됨)',
          instructor: summaryData.instructor || '미지정',
          attendeeCnt: summaryData.attendeeCnt || '0',
          completeCnt: summaryData.completeCnt || '0',
          content: summaryData.content || '작성된 교육 내용 요약이 없습니다.',
          author: userDisplayName,
          createdAt: new Date().toISOString()
        }]);
      } else {
        setTrainings(data || []);
      }
    } catch (err) {
      console.error('fetchTrainings Error:', err);
    }
  }, [assessmentId, token, summaryData, userDisplayName]);

  useEffect(() => {
    fetchTrainings();
  }, [fetchTrainings]);

  // 검색 실행 핸들러
  const handleSearch = useCallback((category, query) => {
    setAppliedSearchQuery(query);
    setCurrentPage(1);
    fetchTrainings(category, query);
  }, [fetchTrainings]);

  // 초기화 핸들러
  const handleReset = useCallback(() => {
    setSearchQuery('');
    setAppliedSearchQuery('');
    setCurrentPage(1);
    fetchTrainings();
  }, [fetchTrainings]);

  const formatCreatedDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) {
        // Fallback for strings like "2026. 08. 11. 오후 1:26:57" or similar
        const match = dateString.match(/\d+/g);
        if (match && match.length >= 3) {
          return match.slice(0, 3).map(n => n.padStart(2, '0')).join('-');
        }
        return dateString;
      }
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    } catch {
      return dateString;
    }
  };

  const totalItems = trainings.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / 10));
  const paginatedTrainings = trainings.slice((currentPage - 1) * 10, currentPage * 10);

  // 신규 작성
  const handleOpenWrite = () => {
    setSelectedDoc(null);
    setModalMode('WRITE');
    setIsModalOpen(true);
  };

  // 등록 및 수정 저장
  const handleSaveDoc = async (docData) => {
    try {
      const payload = {
        ...docData,
        assessmentId,
        author: userDisplayName
      };
      if (modalMode === 'WRITE' && selectedDoc) {
        if (selectedDoc.id !== 'train-init') {
          payload.id = selectedDoc.id;
        }
      }
      await saveSafetyTraining(payload, token);
      setIsModalOpen(false);
      alert(modalMode === 'WRITE' && selectedDoc && selectedDoc.id !== 'train-init' ? '교육일지가 수정되었습니다.' : '교육일지가 등록되었습니다.');
      fetchTrainings();
    } catch (err) {
      console.error('handleSaveDoc Error:', err);
      alert('저장 중 오류가 발생했습니다.');
    }
  };

  // 상세 조회
  const handleOpenDetail = (doc) => {
    setSelectedDoc(doc);
    setModalMode('DETAIL');
    setIsModalOpen(true);
  };

  // 삭제
  const handleDeleteDoc = async (id) => {
    if (id === 'train-init') {
      alert('이관된 초기 임시 데이터는 삭제할 수 없습니다. DB에 저장 후 삭제해 주세요.');
      return;
    }
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try {
      await deleteSafetyTraining(id, token);
      setIsModalOpen(false);
      alert('삭제되었습니다.');
      fetchTrainings();
    } catch (err) {
      console.error('handleDeleteDoc Error:', err);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const handleMouseEnterInstructor = (e, list) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setHoveredInstructor({
      list,
      x: rect.left + rect.width / 2,
      y: rect.top
    });
  };

  const handleMouseLeaveInstructor = () => {
    setHoveredInstructor(null);
  };

  const renderInstructor = (instructorStr) => {
    if (!instructorStr) return '-';

    const list = instructorStr.split(',').map(s => s.trim()).filter(Boolean);
    let displayText = instructorStr;
    if (list.length > 1) {
      displayText = `${list[0]} 외 ${list.length - 1}명`;
    }

    const badgeStyle = {
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      background: '#f4f5f7', padding: '4px 8px', borderRadius: '4px',
      fontSize: '0.8rem', color: '#334155', fontWeight: 600,
      maxWidth: '100%', overflow: 'hidden'
    };

    if (list.length <= 1) {
      return (
        <div style={badgeStyle} title={instructorStr}>
          <i className="fas fa-user" style={{ color: '#6b7280', flexShrink: 0, fontSize: '0.85rem' }}></i>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayText}</span>
        </div>
      );
    }

    return (
      <div
        style={{ display: 'inline-block', maxWidth: '100%', cursor: 'pointer' }}
        onMouseEnter={(e) => handleMouseEnterInstructor(e, list)}
        onMouseLeave={handleMouseLeaveInstructor}
      >
        <div style={badgeStyle}>
          <i className="fas fa-user" style={{ color: '#6b7280', flexShrink: 0, fontSize: '0.85rem' }}></i>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayText}</span>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="sf-card" style={{ padding: 0 }}>
        {/* 컨트롤 패널 (검색 좌측, 버튼 우측) */}
        <div className="sf-card-header" style={{ padding: '16px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'nowrap', gap: '16px', overflow: 'hidden' }}>

          {/* 왼쪽: 검색 영역 */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', position: 'relative', flexShrink: 1, minWidth: 0, flexWrap: 'wrap' }}>
            <select
              value={searchCategory}
              onChange={e => { setSearchCategory(e.target.value); setCurrentPage(1); }}
              style={{ height: '38px', padding: '0 32px 0 12px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem', color: '#475569', outline: 'none', backgroundColor: '#fff', cursor: 'pointer', appearance: 'none', backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23475569%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px top 50%', backgroundSize: '10px auto', flexShrink: 0 }}
            >
              <option value="ALL">전체</option>
              <option value="TITLE">교육종류(제목)</option>
              <option value="INSTRUCTOR">교육강사</option>
              <option value="LOCATION">교육장소</option>
              <option value="AUTHOR">작성자</option>
            </select>
            <input
              type="text"
              placeholder=""
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              style={{ width: '220px', height: '38px', padding: '0 12px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '0.85rem', outline: 'none', backgroundColor: '#fff', flexShrink: 1, minWidth: '80px' }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSearch(searchCategory, searchQuery);
                }
              }}
            />
            <button
              onClick={() => { handleSearch(searchCategory, searchQuery); }}
              style={{ height: '38px', padding: '0 16px', background: '#64748b', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
            >
              검색
            </button>
            <button
              onClick={handleReset}
              style={{ height: '38px', padding: '0 16px', background: 'white', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
            >
              초기화
            </button>
            {/* 검색어 뱃지 (초기화 버튼 우측) */}
            {appliedSearchQuery && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 10px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '16px', fontSize: '0.8rem', color: '#1d4ed8', fontWeight: 600 }}>
                <span>'{appliedSearchQuery}' 검색결과</span>
                <i
                  className="fas fa-times"
                  style={{ cursor: 'pointer', background: '#dbeafe', borderRadius: '50%', padding: '2px 4px', fontSize: '0.7rem' }}
                  onClick={handleReset}
                ></i>
              </div>
            )}
          </div>

          {/* 오른쪽: 버튼 영역 */}
          <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            {(!isLocked || user?.role === 'admin') && (
              <button
                onClick={handleOpenWrite}
                style={{ height: '38px', padding: '0 16px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
              >
                새 글 작성
              </button>
            )}
          </div>
        </div>

        {/* 테이블 */}
        <div style={{ padding: 0, maxWidth: '100%' }}>
          <div className="table-responsive" style={{ width: '100%', overflowX: 'auto', display: 'block', WebkitOverflowScrolling: 'touch' }}>
            <table className="sf-table" style={{ fontSize: '0.88rem', minWidth: '980px', width: '100%', tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th style={{ width: '50px', minWidth: '50px', textAlign: 'center' }}>NO</th>
                  <th style={{ textAlign: 'left', paddingLeft: '16px', width: '250px', minWidth: '250px' }}>교육종류(제목)</th>
                  <th style={{ width: '225px', minWidth: '225px', textAlign: 'center' }}>교육일시</th>
                  <th style={{ width: '120px', minWidth: '120px', textAlign: 'center' }}>교육장소</th>
                  <th style={{ width: '115px', minWidth: '115px', textAlign: 'center' }}>교육강사</th>
                  <th style={{ width: '90px', minWidth: '90px', textAlign: 'center' }}>이수/대상</th>
                  <th style={{ width: '90px', minWidth: '90px', textAlign: 'center' }}>작성자</th>
                  <th style={{ width: '90px', minWidth: '90px', textAlign: 'center' }}>작성일</th>
                </tr>
              </thead>
              <tbody>
                {trainings.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '48px 24px', borderBottom: '1px solid #e2e8f0' }}>
                      <div className="sf-empty" style={{ padding: 0 }}>
                        <div className="sf-empty-icon" style={{ color: '#cbd5e1' }}>
                          <i className={appliedSearchQuery ? 'fas fa-search' : 'fas fa-folder-open'}></i>
                        </div>
                        <div className="sf-empty-text" style={{ fontSize: '0.85rem' }}>
                          {appliedSearchQuery ? (
                            <>
                              <span style={{ color: '#2563eb', fontWeight: 700 }}>[{appliedSearchQuery}]</span>
                              {' '}검색결과가 없습니다.
                            </>
                          ) : '등록된 교육일지가 없습니다.'}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedTrainings.map((doc, idx) => (
                    <tr key={doc.id} onClick={() => handleOpenDetail(doc)} style={{ cursor: 'pointer', borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ textAlign: 'center' }}>
                        <span className="sf-row-num">{totalItems - ((currentPage - 1) * 10 + idx)}</span>
                      </td>
                      <td style={{ verticalAlign: 'middle', maxWidth: '250px' }} title={doc.type || doc.title}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', position: 'relative' }}>
                          <span style={{ fontWeight: 700, color: 'var(--sf-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {doc.type || doc.title}
                          </span>
                          {doc.files && doc.files.length > 0 && (
                            <div
                              style={{
                                flexShrink: 0,
                                color: filePopover.id === doc.id ? '#1d4ed8' : '#94a3b8',
                                fontSize: '0.8rem',
                                cursor: 'pointer',
                                padding: '4px 6px',
                                background: filePopover.id === doc.id ? '#dbeafe' : 'transparent',
                                borderRadius: '4px',
                                transition: 'all 0.2s ease'
                              }}
                              title={`첨부파일`}
                              onMouseOver={(e) => {
                                if (filePopover.id !== doc.id) {
                                  e.currentTarget.style.background = '#eff6ff';
                                  e.currentTarget.style.color = '#2563eb';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (filePopover.id !== doc.id) {
                                  e.currentTarget.style.background = 'transparent';
                                  e.currentTarget.style.color = '#94a3b8';
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (filePopover.id === doc.id) {
                                  setFilePopover({ id: null, x: 0, y: 0 });
                                } else {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  const popoverHeight = Math.min(doc.files.length * 36, 180) + 110; // 실제 파일 개수에 따른 동적 높이 계산
                                  const spaceBelow = window.innerHeight - rect.bottom;
                                  const showUp = spaceBelow < popoverHeight && rect.top > popoverHeight;

                                  setFilePopover({
                                    id: doc.id,
                                    x: rect.left,
                                    isUp: showUp,
                                    top: rect.bottom + 4,
                                    bottom: window.innerHeight - rect.top + 4
                                  });
                                }
                              }}
                            >
                              <i className="fas fa-paperclip"></i>
                            </div>
                          )}

                          {/* 팝오버 UI (React Portal을 이용해 body에 렌더링) */}
                          {filePopover.id === doc.id && doc.files && doc.files.length > 0 && createPortal(
                            <div
                              style={{
                                position: 'fixed',
                                top: filePopover.isUp ? 'auto' : `${filePopover.top}px`,
                                bottom: filePopover.isUp ? `${filePopover.bottom}px` : 'auto',
                                left: `${filePopover.x}px`,
                                background: 'white',
                                border: '1px solid #cbd5e1',
                                borderRadius: '6px',
                                boxShadow: '0 4px 10px -1px rgba(0, 0, 0, 0.15)',
                                zIndex: 9999, // 최상위 z-index
                                width: '380px', // 파일명과 크기를 위해 넓이 넉넉히 확장
                                padding: '10px',
                                cursor: 'default'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {(() => {
                                const filesList = doc.files;
                                return (
                                  <>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#e2e8f0', margin: '-10px -10px 10px -10px', padding: '8px 12px', borderTopLeftRadius: '6px', borderTopRightRadius: '6px', borderBottom: '1px solid #cbd5e1' }}>
                                      <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569' }}>첨부파일 목록 ({filesList.length})</span>
                                      <i className="fas fa-times" style={{ cursor: 'pointer', color: '#64748b', fontSize: '0.9rem' }} onClick={() => setFilePopover({ id: null, x: 0, y: 0 })}></i>
                                    </div>
                                    <div style={{ maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      {filesList.map((mf, mfIdx) => (
                                        <div key={mf.id || mfIdx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', borderRadius: '4px', fontSize: '0.75rem', color: '#475569', background: '#f8fafc', border: '1px solid #f1f5f9' }}>
                                          <a href="#!" onClick={(e) => { e.preventDefault(); handleView(mf); }} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '240px', color: '#1e293b', textDecoration: 'none', display: 'flex', alignItems: 'center' }} title={`${mf.originalName || mf.name} 열기`}>
                                            <i className="far fa-file-alt" style={{ marginRight: '6px', color: '#64748b' }}></i>
                                            <span style={{ cursor: 'pointer' }} onMouseOver={e => e.currentTarget.style.textDecoration = 'underline'} onMouseOut={e => e.currentTarget.style.textDecoration = 'none'}>{mf.originalName || mf.name}</span>
                                          </a>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{formatBytes(mf.size)}</span>
                                            <i className="fas fa-download" style={{ color: '#2563eb', cursor: 'pointer' }} title="다운로드" onClick={() => handleDownload(mf)}></i>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e2e8f0' }}>
                                      <button
                                        onClick={() => handleZipDownload(filesList, doc.title || doc.type)}
                                        style={{
                                          width: '100%', padding: '8px 0', background: '#eff6ff', border: '1px solid #bfdbfe',
                                          borderRadius: '4px', color: '#1d4ed8', fontSize: '0.75rem', fontWeight: 700,
                                          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                                        }}>
                                        <i className="fas fa-file-archive" style={{ color: '#ea580c', fontSize: '0.85rem' }}></i>
                                        {filesList.length === 1 ? '압축(ZIP) 다운로드' : '전체 압축(ZIP) 다운로드'}
                                      </button>
                                    </div>
                                  </>
                                );
                              })()}
                            </div>,
                            document.body
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: 'center', fontSize: '0.84rem', color: 'var(--sf-text)', maxWidth: '225px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={doc.time ? `${doc.date} (${doc.time})` : (doc.date || '-')}>
                        {doc.time ? `${doc.date} (${doc.time})` : (doc.date || '-')}
                      </td>
                      <td style={{ textAlign: 'center', fontSize: '0.84rem', color: 'var(--sf-text)', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={doc.location || '-'}>
                        {doc.location || '-'}
                      </td>
                      <td style={{ textAlign: 'center', maxWidth: '115px', verticalAlign: 'middle' }} onClick={(e) => e.stopPropagation()}>
                        {renderInstructor(doc.instructor)}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className="sf-badge sf-badge-outline-primary" style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 800, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8', whiteSpace: 'nowrap' }}>
                          {doc.completeCnt || 0}명 / {doc.attendeeCnt || 0}명
                        </span>
                      </td>
                      <td style={{ textAlign: 'center', maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={doc.author || '-'}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#f4f5f7', padding: '4px 8px', borderRadius: '4px', fontSize: '0.8rem', color: '#334155', fontWeight: 600, maxWidth: '100%', overflow: 'hidden' }}>
                          <i className="fas fa-user" style={{ color: '#6b7280', flexShrink: 0, fontSize: '0.85rem' }}></i>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.author || '-'}</span>
                        </div>
                      </td>
                      <td style={{ textAlign: 'center', fontSize: '0.84rem', color: 'var(--sf-text)', maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={formatCreatedDate(doc.createdAt)}>
                        {formatCreatedDate(doc.createdAt)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* 페이징 */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid var(--sf-border-light)', background: '#f8fafc',
          display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative'
        }}>
          <div style={{
            position: 'absolute', left: '24px',
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: '#eff6ff', border: '1px solid #bfdbfe',
            padding: '4px 12px', borderRadius: '20px',
            fontSize: '0.8rem', color: '#1e3a8a', fontWeight: 600,
            boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
          }}>
            <span style={{ color: totalItems === 0 ? '#e2e8f0' : '#2563eb' }}>{totalItems === 0 ? 0 : currentPage}</span>
            <span style={{ color: totalItems === 0 ? '#e2e8f0' : '#93c5fd', margin: '0 -2px' }}>/</span>
            <span>{totalItems === 0 ? 0 : Math.max(1, totalPages)}</span>
            <span style={{ color: totalItems === 0 ? '#e2e8f0' : '#bfdbfe', margin: '0 2px' }}>|</span>
            <span>총 {totalItems}건</span>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            {(() => {
              const PAGE_BLOCK_SIZE = 10;
              const safeTotalPages = Math.max(1, totalPages);
              const startPage = Math.floor((currentPage - 1) / PAGE_BLOCK_SIZE) * PAGE_BLOCK_SIZE + 1;
              const endPage = Math.min(startPage + PAGE_BLOCK_SIZE - 1, safeTotalPages);
              const pagesInBlock = Array.from({ length: (endPage - startPage + 1) }, (_, i) => startPage + i);

              return (
                <>
                  {/* 처음으로 */}
                  <button
                    className="sf-btn sf-btn-outline sf-btn-sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1 || totalItems === 0}
                    style={{
                      opacity: (currentPage === 1 || totalItems === 0) ? 0.4 : 1,
                      cursor: (currentPage === 1 || totalItems === 0) ? 'default' : 'pointer',
                      pointerEvents: (currentPage === 1 || totalItems === 0) ? 'none' : 'auto',
                      minWidth: 36, padding: '0 8px'
                    }}
                    title="처음으로"
                  >
                    <i className="fas fa-angle-double-left"></i>
                  </button>
                  {/* 이전 블록 */}
                  <button
                    className="sf-btn sf-btn-outline sf-btn-sm"
                    onClick={() => setCurrentPage(Math.max(startPage - 1, 1))}
                    disabled={startPage === 1 || totalItems === 0}
                    style={{
                      opacity: (startPage === 1 || totalItems === 0) ? 0.4 : 1,
                      cursor: (startPage === 1 || totalItems === 0) ? 'default' : 'pointer',
                      pointerEvents: (startPage === 1 || totalItems === 0) ? 'none' : 'auto',
                      minWidth: 36, padding: '0 8px'
                    }}
                    title="이전 10페이지"
                  >
                    <i className="fas fa-angle-left"></i>
                  </button>

                  {pagesInBlock.map(page => (
                    <button
                      key={page}
                      className={`sf-btn sf-btn-sm ${currentPage === page && totalItems > 0 ? 'sf-btn-primary' : 'sf-btn-outline'}`}
                      onClick={() => setCurrentPage(page)}
                      disabled={totalItems === 0}
                      style={{
                        minWidth: 36, borderRadius: 8,
                        opacity: totalItems === 0 ? 0.5 : 1,
                        cursor: totalItems === 0 ? 'default' : 'pointer',
                        pointerEvents: totalItems === 0 ? 'none' : 'auto'
                      }}
                    >
                      {page}
                    </button>
                  ))}

                  {/* 다음 블록 */}
                  <button
                    className="sf-btn sf-btn-outline sf-btn-sm"
                    onClick={() => setCurrentPage(Math.min(endPage + 1, safeTotalPages))}
                    disabled={endPage === safeTotalPages || totalItems === 0}
                    style={{
                      opacity: (endPage === safeTotalPages || totalItems === 0) ? 0.4 : 1,
                      cursor: (endPage === safeTotalPages || totalItems === 0) ? 'default' : 'pointer',
                      pointerEvents: (endPage === safeTotalPages || totalItems === 0) ? 'none' : 'auto',
                      minWidth: 36, padding: '0 8px'
                    }}
                    title="다음 10페이지"
                  >
                    <i className="fas fa-angle-right"></i>
                  </button>
                  {/* 마지막으로 */}
                  <button
                    className="sf-btn sf-btn-outline sf-btn-sm"
                    onClick={() => setCurrentPage(safeTotalPages)}
                    disabled={currentPage === safeTotalPages || totalItems === 0}
                    style={{
                      opacity: (currentPage === safeTotalPages || totalItems === 0) ? 0.4 : 1,
                      cursor: (currentPage === safeTotalPages || totalItems === 0) ? 'default' : 'pointer',
                      pointerEvents: (currentPage === safeTotalPages || totalItems === 0) ? 'none' : 'auto',
                      minWidth: 36, padding: '0 8px'
                    }}
                    title="마지막으로"
                  >
                    <i className="fas fa-angle-double-right"></i>
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <SafetyTrainingModal
          mode={modalMode}
          initialData={selectedDoc}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveDoc}
          onDelete={() => handleDeleteDoc(selectedDoc?.id)}
          onStartEdit={() => setModalMode('WRITE')}
          canEdit={selectedDoc ? hasEditPermission(selectedDoc) : !isLocked}
        />
      )}

      {/* 포탈 툴팁 (최상위 렌더링) */}
      {hoveredInstructor && createPortal(
        <div
          style={{
            position: 'fixed',
            top: hoveredInstructor.y - 8,
            left: hoveredInstructor.x,
            transform: 'translate(-50%, -100%)',
            backgroundColor: '#2d3748',
            borderRadius: '8px',
            padding: '12px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5)',
            zIndex: 999999,
            width: 'max-content',
            maxWidth: '300px',
            pointerEvents: 'none'
          }}
        >
          <div style={{ marginBottom: '8px' }}>
            <span className="badge" style={{ backgroundColor: '#4f46e5', color: '#fff', fontSize: '0.75rem', padding: '4px 8px' }}>교육강사</span>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {hoveredInstructor.list.map((name, i) => (
              <span key={i} className="badge" style={{ backgroundColor: '#4a5568', color: '#fff', fontSize: '0.8rem', padding: '6px 10px', fontWeight: 'normal' }}>
                {name}
              </span>
            ))}
          </div>
          <div style={{
            position: 'absolute', top: '100%', left: '50%', marginLeft: '-6px',
            borderWidth: '6px', borderStyle: 'solid', borderColor: '#2d3748 transparent transparent transparent'
          }}></div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default SafetyTrainingTab;
